'use client';

import { useEffect, useMemo, useState } from 'react';
import { AuthTokenStorageService } from '@/lib/auth';
import { savePendingAuthRedirect } from '@/lib/auth/pending-auth-redirect';
import { useAuthMe } from '@/features/user/api/user.queries';
import { setOpenLoginModal } from '@/stores';
import {
  GUEST_LIMIT_MESSAGES,
  GuestLimiter,
  getRestrictedPageKey,
  getSearchSignature,
  guestJobViewLimiter,
  guestSearchLimiter,
  loginRequiredLimiter,
} from '@/features/jobs/lib/guest-limits';

type GateStatus = 'allowed' | 'pending' | 'blocked';

export interface GuestGate {
  isAllowed: boolean;
  isPending: boolean;
  /** Хязгаарлагдсан үед харуулах текст, үгүй бол `null`. */
  blockedMessage: string | null;
}

/** Нэвтрэх modal нээж, нэвтэрсний дараа одоогийн хуудас руу буцахаар тэмдэглэнэ. */
export function promptLoginAndReturn() {
  savePendingAuthRedirect();
  setOpenLoginModal(true);
}

/**
 * Нэвтрээгүй хэрэглэгчийн үйлдлийг `limiter`-ээр хязгаарлана. `key === null` бол хязгааргүй.
 * Шийдвэрийг effect дотор гаргадаг тул (localStorage) эхний render `pending` байна.
 */
function useGuestLimitGate(
  limiter: GuestLimiter,
  key: string | null,
  message: string,
  enabled: boolean
): GuestGate {
  const { data: user } = useAuthMe();
  const isLoggedIn = Boolean(user);
  const needsCheck = enabled && key !== null;

  const [decision, setDecision] = useState<{
    key: string;
    status: Exclude<GateStatus, 'pending'>;
  } | null>(null);

  useEffect(() => {
    if (!needsCheck || key === null) return;

    if (isLoggedIn || AuthTokenStorageService.getAccessToken()) {
      setDecision({ key, status: 'allowed' });
      return;
    }

    const allowed = limiter.consume(key);
    setDecision({ key, status: allowed ? 'allowed' : 'blocked' });
    if (!allowed) promptLoginAndReturn();
  }, [limiter, needsCheck, key, isLoggedIn]);

  let status: GateStatus = 'allowed';
  if (needsCheck) {
    const isStale =
      decision?.key !== key || (isLoggedIn && decision.status === 'blocked');
    status = isStale ? 'pending' : decision.status;
  }

  return {
    isAllowed: status === 'allowed',
    isPending: status === 'pending',
    blockedMessage: status === 'blocked' ? message : null,
  };
}

/** Хайлт: 3 дахь хуудаснаас нэвтрэх шаардлагатай, мөн 4 цагт ялгаатай 3 хайлт. */
export function useGuestSearchGate(
  params: Record<string, unknown>,
  enabled = true
): GuestGate {
  const signature = useMemo(() => getSearchSignature(params), [params]);
  const pageKey = useMemo(() => getRestrictedPageKey(params), [params]);

  const pageGate = useGuestLimitGate(
    loginRequiredLimiter,
    pageKey,
    GUEST_LIMIT_MESSAGES.page,
    enabled
  );
  // Нэвтрэх шаардлагатай хуудсан дээр хайлтын эрхийг дэмий зарцуулахгүй
  const searchGate = useGuestLimitGate(
    guestSearchLimiter,
    signature,
    GUEST_LIMIT_MESSAGES.search,
    enabled && pageKey === null
  );

  return {
    isAllowed: pageGate.isAllowed && searchGate.isAllowed,
    isPending: pageGate.isPending || searchGate.isPending,
    blockedMessage: pageGate.blockedMessage ?? searchGate.blockedMessage,
  };
}

/** Зар үзэх: 4 цагт ялгаатай 10 зар, үзсэн зараа дахин нээж болно. */
export function useGuestJobViewGate(
  code: string | number,
  enabled = true
): GuestGate {
  const key = code === '' ? null : String(code);
  return useGuestLimitGate(
    guestJobViewLimiter,
    key,
    GUEST_LIMIT_MESSAGES.jobView,
    enabled
  );
}
